import { Alert, Button, Select } from '@dittolive/anvil'
import { graphql } from 'babel-plugin-relay/macro'
import { useAppToDatabaseRename } from 'components/hooks'
import React, { useState } from 'react'
import { usePortalMutation } from 'utils'

import { AppOrganizationSelectDestinationInputTransferMutation } from './__generated__/AppOrganizationSelectDestinationInputTransferMutation.graphql'
import { TransferAppDestinationSelectorFragment_organization$data } from './__generated__/TransferAppDestinationSelectorFragment_organization.graphql'
import TransferAppConfirmationModal from './TransferAppConfirmationModal'

const transferMutation = graphql`
  mutation AppOrganizationSelectDestinationInputTransferMutation(
    $appId: ID!
    $organizationId: ID!
  ) {
    requestAppTransferToOrganization(
      appId: $appId
      organizationId: $organizationId
    ) {
      transferRequestDestination {
        ... on Organization {
          imageUrl
          name
        }
      }
    }
  }
`

type Props = {
  /** App ID */
  appId: string
  /** List of user organizations */
  organizations: readonly TransferAppDestinationSelectorFragment_organization$data[]
}

const AppOrganizationSelectDestinationInput: React.FC<Props> = ({
  appId,
  organizations,
}) => {
  const { databaseText } = useAppToDatabaseRename()

  const [transferApp, isTransferringApp] =
    usePortalMutation<AppOrganizationSelectDestinationInputTransferMutation>(
      transferMutation,
      {
        mutationName: 'RequestAppTransferToOrganization',
        sentryContext: {
          component: 'AppOrganizationSelectDestinationInput',
        },
        showErrorToast: false,
      },
    )
  const [destinationOrganization, setDestinationOrganization] =
    useState<TransferAppDestinationSelectorFragment_organization$data | null>(
      null,
    )
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)
  const [transferError, setTransferError] = useState<Error>()

  const handleTransferClicked = () => {
    setTransferError(undefined)
    setIsConfirmationOpen(true)
  }

  const handleConfirmationClosed = (confirmTransfer: boolean) => {
    if (confirmTransfer && destinationOrganization) {
      transferApp({
        variables: { appId, organizationId: destinationOrganization.id },
        onCompleted: () => {
          setIsConfirmationOpen(false)
        },
        onError: (err) => {
          setTransferError(err)
        },
      })
    } else {
      setIsConfirmationOpen(false)
    }
  }

  return (
    <>
      {!!transferError && (
        <Alert
          autoDismiss={false}
          variant="danger"
        >{`Could not create the ${databaseText} transfer request: ${transferError.message}`}</Alert>
      )}
      <TransferAppConfirmationModal
        isOpen={isConfirmationOpen}
        onClose={handleConfirmationClosed}
        appId={appId}
        destination={destinationOrganization?.slug || ''}
        isProcessing={isTransferringApp}
      />

      <Select
        id="organizationSelect"
        label="Organization"
        options={(organizations || []).map((org) => ({
          value: org.id || '',
          label: (
            <div className="flex items-center space-x-3">
              <div className="relative rounded-full">
                <img
                  className="h-6 w-6 rounded-full object-cover"
                  alt={`${org.name} avatar`}
                  src={org.imageUrl || ''}
                />
              </div>
              <div>{org.name}</div>
            </div>
          ),
        }))}
        onValueChange={(value) => {
          const selectedOrg =
            organizations?.find((org) => org.id === value) || null

          setDestinationOrganization(selectedOrg)
        }}
        value={destinationOrganization?.id || ''}
        data-testid="searchOrganizationsAsyncSelect"
      />

      <Button
        disabled={!destinationOrganization}
        onClick={handleTransferClicked}
        className="mt-8"
        variant="critical"
      >
        Transfer
      </Button>
    </>
  )
}

export default AppOrganizationSelectDestinationInput
